import { useEffect, useRef, useState } from 'react';
import { PrimaryButton, SecondaryButton, TextButton } from '../components/Button';
import { FieldNote } from '../components/Field';
import { Screen } from '../components/Screen';
import { copy } from '../copy';
import { openExternal, telHref } from '../lib/links';
import {
  FULL_PARCEL_PATTERN,
  TAIL_LENGTH,
  formatTailGroups,
  parcelPrefix,
  sanitiseTailInput,
} from '../lib/parcel';
import { handleScannedCode, manualErrorFor } from '../state/scanning';
import { useAppStore, type ManualEntryReason } from '../state/store';

const PLANNING_PHONE: string = import.meta.env.VITE_PLANNING_PHONE ?? '';

/** De terugvalregel van F7, letterlijk uit copy.md paragraaf Scanner. */
function fallbackLine(reason: ManualEntryReason): string | null {
  if (reason === 'cameraDenied') return copy.scanner.denied;
  if (reason === 'cameraFailed') return copy.scanner.failed;
  return null;
}

/** G1–G3 — Handmatig invoeren. De letter O wordt geweigerd, nooit omgezet. */
export function ManualScreen() {
  const route = useAppStore((s) => s.route);
  const purpose = useAppStore((s) => s.scanPurpose);
  const stopId = useAppStore((s) => s.stopId);
  const navigate = useAppStore((s) => s.navigate);
  const setScanOutcome = useAppStore((s) => s.setScanOutcome);
  const manualEntryReason = useAppStore((s) => s.manualEntryReason);

  const inputRef = useRef<HTMLInputElement>(null);
  const [tail, setTail] = useState('');
  const [full, setFull] = useState('');
  const [fullMode, setFullMode] = useState(false);
  const [confusable, setConfusable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [fullMode]);

  // De vaste kop komt uit de gecachte rit; hij verschilt per bestemming (D75).
  const stop = route?.stops.find((candidate) => candidate.id === stopId) ?? route?.stops[0];
  const prefix = parcelPrefix(stop?.parcels[0]?.parcelNumber ?? '') || 'GBNL-2026-AMS-';

  const fallback = fallbackLine(manualEntryReason);
  const remaining = TAIL_LENGTH - tail.length;
  const fullValid = FULL_PARCEL_PATTERN.test(full.trim().toUpperCase());
  const ready = fullMode ? fullValid : remaining === 0;

  const disabledReason = ready
    ? null
    : fullMode
      ? copy.manual.fullLabel
      : copy.manual.remaining(remaining);

  const submit = () => {
    if (!ready) return;
    const code = fullMode ? full.trim().toUpperCase() : `${prefix}${tail}`;
    void handleScannedCode(code).then((outcome) => {
      const inline = manualErrorFor(outcome);
      if (inline) {
        setError(inline);
        return;
      }
      setError(null);
      if (outcome.kind === 'loaded') {
        // Bij laden blijft de bezorger in de lijst: de teller loopt op, meer is er niet.
        setTail('');
        setFull('');
        navigate('load');
        return;
      }
      setScanOutcome(outcome);
      navigate('scanresult');
    });
  };

  return (
    <Screen
      name="manual"
      foot={
        <>
          <PrimaryButton disabledReason={disabledReason} onClick={submit}>
            {purpose === 'load' ? copy.manual.submitLoad : copy.manual.submit}
          </PrimaryButton>
          {error ? (
            <div className="row" style={{ gap: 12 }}>
              <div style={{ flex: 1 }}>
                <SecondaryButton
                  disabledReason={PLANNING_PHONE ? null : copy.stop.noPhone}
                  onClick={() => openExternal(telHref(PLANNING_PHONE))}
                >
                  {copy.manual.callPlanning}
                </SecondaryButton>
              </div>
              <div style={{ flex: 1 }}>
                <SecondaryButton
                  onClick={() => {
                    setError(null);
                    setTail('');
                    setFull('');
                    inputRef.current?.focus();
                  }}
                >
                  {copy.manual.again}
                </SecondaryButton>
              </div>
            </div>
          ) : (
            <TextButton center onClick={() => navigate('scanner')}>
              {copy.manual.backToScanner}
            </TextButton>
          )}
        </>
      }
    >
      <div className="row row--between pad-x" style={{ paddingTop: 14 }}>
        <h1 className="t-serif" style={{ fontSize: 26 }}>
          {copy.manual.title}
        </h1>
        <TextButton onClick={() => navigate(purpose === 'load' ? 'load' : 'stop')}>
          {copy.scanner.close}
        </TextButton>
      </div>

      <div className="stack pad-x" style={{ paddingTop: 16, gap: 12 }}>
        {fallback ? (
          <p className="t-caption o-65" style={{ margin: 0 }} role="status">
            {fallback}
          </p>
        ) : null}
        {fullMode ? (
          <>
            <div className={`field${error ? ' field--error' : ''}`} style={{ padding: 16 }}>
              <label className="field__label" htmlFor="manual-full">
                {copy.manual.fullLabel}
              </label>
              <input
                ref={inputRef}
                id="manual-full"
                className="field__input t-mono"
                style={{ fontSize: 18 }}
                value={full}
                maxLength={28}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
                aria-describedby="manual-explain"
                onChange={(event) => setFull(event.target.value.toUpperCase())}
              />
            </div>
          </>
        ) : (
          <div className={`field${error ? ' field--error' : ''}`} style={{ padding: 16 }}>
            <label className="field__label" htmlFor="manual-tail">
              {copy.manual.label}
            </label>
            <div className="row" style={{ gap: 6, alignItems: 'baseline' }}>
              <span
                className="t-mono"
                style={{
                  fontSize: 17,
                  color: 'var(--text-50)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  letterSpacing: 0,
                }}
              >
                {prefix}
              </span>
              <input
                ref={inputRef}
                id="manual-tail"
                className="field__input t-mono"
                style={{ fontSize: 20, letterSpacing: 2, minWidth: 0 }}
                value={formatTailGroups(tail)}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
                aria-describedby="manual-explain"
                onChange={(event) => {
                  const result = sanitiseTailInput(event.target.value);
                  setTail(result.value);
                  setConfusable(result.rejectedConfusable);
                  if (error) setError(null);
                }}
              />
            </div>
          </div>
        )}

        {error ? (
          <FieldNote marker>{error}</FieldNote>
        ) : (
          <p
            id="manual-explain"
            className="t-caption"
            style={{ color: confusable ? 'var(--color-terracotta)' : 'var(--text-65)', margin: 0 }}
            role={confusable ? 'alert' : undefined}
          >
            {copy.manual.explain}
          </p>
        )}

        {!fullMode ? (
          <TextButton onClick={() => setFullMode(true)}>{copy.manual.full}</TextButton>
        ) : null}
      </div>
    </Screen>
  );
}
